import { Circle, Download, Info, Receipt, Upload } from '@mui/icons-material';
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import ExcelJS from 'exceljs';
import { getDocumentStatus } from 'lib/api/sii';
import { formatMoney } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getInvoicingBookReport } from '../../../../../../lib/api/reports';
import { getSelectedCertificate } from '../../../../../../lib/api/SIICertificates';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../lib/designSystem/Table';
import { LightTooltip } from '../../../../../../lib/designSystem/WhiteTooltip';
import TheBillerFinalizedModal from '../../../SII/components/TheBiller/components/finalizedModal';
import ReportInvoicingConfirmDialog from './components/confirmDialog';
import DteStatusDialog from './components/DteStatusDialog';
import MonthYearSearcher from './components/MonthYearSearcher';
import SubjectOptionsDialog from './components/SubjectOptionsDialog';

dayjs.locale('es');

const mockeInvoiced = [
  {
    id: '77777019-5/1033/33',
    businessIdentifier: '77777019-5',
    stakeholderIdentifier: '59283380-8',
    stakeholderName: 'Avio Spa',
    docType: '33',
    folio: '1033',
    issuedDate: '2025-09-30',
    amount: 50000,
    amountWithIva: 59500,
    commune: 'Comuna 1',
    address: 'Dirección 1',
    email: 'Email 1',
    status: 'pending',
    details: [
      {
        subject: 'Servicios Factoring',
        qty: 1,
        amount: 50000,
      },
    ],
  },
];

const InvoicingBook: React.FC = () => {
  const [data, setData] = useState([]);
  const [selectedIds, setSelectedIds] = useState<any>([]);
  const [selectedCertificate, setSelectedCertificate] = useState<any>(null);
  const [selected, setSelected] = useState([]);
  const [openInvoiceDialog, setOpenInvoiceDialog] = useState(false);
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month());
  const [startDate, setStartDate] = useState(dayjs().startOf('month'));
  const [endDate, setEndDate] = useState(dayjs().endOf('month'));
  const [loading, setLoading] = useState(true);
  const [successInvoices, setSuccessInvoices] = useState<any>([]);
  const [resultInvoices, setResultInvoices] = useState<any>([]);
  const [finalizedModal, setFinalizedModal] = useState(false);
  const [dteStatusDialogOpen, setDteStatusDialogOpen] = useState(false);
  const [dteStatus, setDteStatus] = useState<any>(null);
  const [subjectDialogOpen, setSubjectDialogOpen] = useState(false);
  const [alreadyInvoicedInvoices, setAlreadyInvoicedInvoices] = useState<any>(
    [],
  );
  const [invoicingPartner, setInvoicingPartner] = useState<any>(null);
  const dispatch = useDispatch();
  const [type, setType] = useState<'Por facturar' | 'Facturado'>(
    'Por facturar',
  );

  const handleClose = () => {
    setFinalizedModal(false);
  };

  const exportToExcel = async () => {
    const selectedInvoices = data.filter((item: any) =>
      selectedIds.includes(item.id),
    );
    const plainData: any = [];
    selectedInvoices.forEach((item: any) => {
      plainData.push([
        'FACTURA',
        item.businessIdentifierRv,
        item.businessIdentifierDv,
        item.businessName,
        item.index,
        item.invoicingDate,
        item.invoicingDate,
        item.totalCosts,
        item.totalCostsIva,
        item.totalCostsWithIva,
        '1',
        item.commune,
        item.address,
        item.email,
        'Sin informacion',
      ]);
      plainData.push([
        'DETALLE',
        item.index,
        'Servicios Factoring',
        '1',
        '1',
        item.totalCosts,
        item.totalCosts,
      ]);
    });
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet1');

    // Add data to worksheet
    [...plainData].forEach((rowData) => {
      console.log(rowData);
      worksheet.addRow(rowData);
    });
    // Save workbook
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = 'data.xlsx';
    a.click();

    URL.revokeObjectURL(url);
  };

  const setUp = async () => {
    setLoading(true);
    const start = dayjs(new Date(year, month, 1)).startOf('month');
    const end = dayjs(new Date(year, month, 1)).endOf('month');
    setStartDate(start);
    setEndDate(end);
    try {
      const {
        completeInvoices,
        alreadyInvoicedInvoices,
        system_invoicing_partner_type,
      } = await getInvoicingBookReport(
        dispatch,
        start.toString(),
        end.toString(),
      );
      const responseWithId = completeInvoices.map(
        (item: any, index: number) => ({
          ...item,
          id: index,
        }),
      );

      const certificateResponse = await getSelectedCertificate(dispatch);
      setSelectedCertificate(certificateResponse);
      setData(responseWithId);
      setAlreadyInvoicedInvoices(alreadyInvoicedInvoices);
      setInvoicingPartner(system_invoicing_partner_type);
    } catch (error) {
      console.log(error);
      alert('Error al obtener los datos');
    }
    setLoading(false);
  };

  const checkDocuemntStatus = async (id: any) => {
    try {
      const row = alreadyInvoicedInvoices.find((item: any) => item.id === id);
      console.log(row);
      if (!row) return;
      const invoices = [
        {
          businessIdentifier: row.businessIdentifier,
          docType: row.docType,
          folio: row.folio,
        },
      ];
      const response = await getDocumentStatus(dispatch, { invoices });
      const changedInvoice = response?.filter(
        (invoice: any) => invoice.statusChanged,
      );
      if (changedInvoice && changedInvoice.length > 0) {
        await setUp();
      }
      setDteStatusDialogOpen(true);
      setDteStatus(response);
      return response;
    } catch (error) {
      console.log(error);
      return 'Error al obtener el estado del documento';
    }
  };

  const handleCheckDoc = {
    key: 'status',
    label: '',
    handleAction: checkDocuemntStatus,
    icon: <Info />,
  };

  const parseComissionsToInvoices = (
    preInvoices: any,
    subjectOption?: 'common' | 'details' | 'perOperation',
    commonSubject?: string,
  ) => {
    const invoices = preInvoices.map((invoice: any, index: number) => {
      const dateYYYYMMDD = endDate.format('YYYY-MM-DD');

      let details = [];

      if (subjectOption === 'common') {
        const subject = commonSubject || 'Servicios Factoring';
        details = [
          {
            subject: subject,
            qty: 1,
            amount: invoice.totalCosts,
          },
        ];
      } else if (subjectOption === 'details' && invoice.allBusinessCosts) {
        // Concatenar requestType + requestId separados por comas
        const subject = 'Comisiones: ';
        const subjectText =
          subject +
          invoice.allBusinessCosts
            .map((cost: any) => `${cost.requestType} ${cost.requestId}`)
            .join(', ');
        details = [
          {
            subject: subjectText,
            qty: 1,
            amount: invoice.totalCosts,
          },
        ];
      } else if (subjectOption === 'perOperation' && invoice.allBusinessCosts) {
        // Crear un detalle por cada operación
        details = invoice.allBusinessCosts.map((cost: any) => ({
          subject: `${cost.requestType} ${cost.requestId}`,
          qty: 1,
          amount: cost.operationCostTotal,
        }));
      } else {
        details = [
          {
            subject: 'Servicios Factoring',
            qty: 1,
            amount: invoice.totalCosts,
          },
        ];
      }

      return {
        index: index + 1,
        identifier: invoice.businessIdentifier,
        name: invoice.businessName,
        issuedDate: dateYYYYMMDD,
        amount: invoice.amountToInvoiceByPruff,
        amountWithIva: invoice.totalCostsWithIva,
        commune: invoice.commune,
        address: invoice.address,
        email: invoice.email,
        details: details,
      };
    });
    return invoices;
  };

  const openConfirmDialog = () => {
    if (!selectedCertificate) {
      alert(
        'No tienes un certificado seleccionado. Para utilizar esta función, anda a configuración y selecciona un certificado.',
      );
      return;
    }
    setSubjectDialogOpen(true);
  };

  const handleSubjectDialogConfirm = (
    option: 'common' | 'details' | 'perOperation',
    commonSubject?: string,
  ) => {
    const selectedInvoices = data.filter((item: any) =>
      selectedIds.includes(item.id),
    );
    const formattedInvoices = parseComissionsToInvoices(
      selectedInvoices,
      option,
      commonSubject,
    );
    setSelectedIds([]);
    setSelected(formattedInvoices);
    setOpenInvoiceDialog(true);
  };

  useEffect(() => {
    setUp();
  }, []);

  const customAlertComponent = (missingInfo: any) => {
    if (!missingInfo) return null;

    const customTitle = (
      <div style={{ padding: '10px' }}>
        <Typography variant="body2" color="textSecondary">
          Desglose emitidas a la empresa
        </Typography>
        <EnhancedTable
          dataHeaders={[
            { key: 'key', label: 'Campo' },
            { key: 'value', label: 'Valor' },
          ]}
          data={missingInfo}
          rowsPerPageDefault={100}
          lastElement
        />
      </div>
    );
    return (
      <LightTooltip title={customTitle} placement="right">
        <Circle color="warning" />
      </LightTooltip>
    );
  };

  const customInvoicingComponent = (emittedInvoices: any) => {
    console.log(emittedInvoices, type);
    if (!emittedInvoices) return null;
    const customTitle = (
      <div style={{ padding: '10px' }}>
        <Typography variant="body2" color="textSecondary">
          Desglose emitidas a la empresa
        </Typography>
        <EnhancedTable
          dataHeaders={[
            { key: 'folio', label: 'Folio' },
            { key: 'code', label: 'Tipo Doc' },
            { key: 'amount', label: 'Monto', type: 'money' },
            { key: 'issuedDate', label: 'Emisión', type: 'date' },
            { key: 'status', label: 'Estado' },
          ]}
          data={emittedInvoices}
          rowsPerPageDefault={100}
          lastElement
        />
      </div>
    );
    const totalEmitted = emittedInvoices.reduce(
      (acc: number, invoice: any) => acc + invoice.amount,
      0,
    );
    return (
      <LightTooltip title={customTitle} placement="right">
        <div
          style={{
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
          }}
        >
          <Typography variant="body1">{formatMoney(totalEmitted)}</Typography>
        </div>
      </LightTooltip>
    );
  };

  const customColumnComponent = (allBusinessCosts: any) => {
    if (!allBusinessCosts) return null;
    const customTitle = (
      <div style={{ padding: '10px' }}>
        <Typography variant="body2" color="textSecondary">
          Desglose comisiones de la empresa
        </Typography>
        <EnhancedTable
          dataHeaders={[
            { key: 'requestId', label: 'Id' },
            { key: 'requestType', label: 'Tipo' },
            {
              key: 'operationCostTotal',
              label: 'Costo operación',
              type: 'money',
            },
            {
              key: 'operationCostTotalIva',
              label: 'IVA Costo',
              type: 'money',
            },
            {
              key: 'operationCostTotalWithIva',
              label: 'Total Costo',
              type: 'money',
            },
            {
              key: 'operationExpensesTotal',
              label: 'Gastos Solicitud',
              type: 'money',
            },
            {
              key: 'operationExpensesTotalIva',
              label: 'IVA Gastos',
              type: 'money',
            },
            {
              key: 'operationExpensesTotalWithIva',
              label: 'Total Gastos',
              type: 'money',
            },
            {
              key: 'financingAt',
              label: 'Contabilizado en',
              type: 'date',
            },
            handleCheckDoc,
          ]}
          data={allBusinessCosts}
          rowsPerPageDefault={100}
          lastElement
          finalSumRow
        />
      </div>
    );
    return (
      <LightTooltip title={customTitle} placement="right">
        <div style={{ cursor: 'pointer' }}>
          <Info />
        </div>
      </LightTooltip>
    );
  };

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        customTitle={
          <MonthYearSearcher
            year={year}
            setYear={setYear}
            month={month}
            setMonth={setMonth}
            setUp={setUp}
          />
        }
        variant="body1"
        buttons={[
          {
            action: () => openConfirmDialog(),
            name: 'Facturar',
            color: 'primary',
            disabled: selectedIds.length === 0,
            startIcon: <Receipt />,
          },
          {
            action: () => exportToExcel(),
            name: 'Descargar XSLX',
            color: 'primary',
            disabled: selectedIds.length === 0,
            startIcon: <Download />,
          },
        ]}
      />
      <Box padding="10px 0px">
        <ToggleButtonGroup
          value={type}
          color="primary"
          exclusive
          onChange={(_, newValue) => {
            setType(newValue);
          }}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              fontWeight: 500,
              minWidth: '140px',
            },
          }}
        >
          <ToggleButton value="Por facturar">Por facturar</ToggleButton>
          <ToggleButton value="Facturado">Facturado</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {type === 'Por facturar' && (
        <EnhancedTable
          dataHeaders={[
            ...(invoicingPartner === 'cessionDTE'
              ? [
                  {
                    key: 'missingInfo',
                    label: 'Datos',
                    customComponent: customAlertComponent,
                    type: 'custom',
                  },
                ]
              : []),
            {
              key: 'allBusinessCosts',
              label: 'Desglose',
              customComponent: customColumnComponent,
              type: 'custom',
            },
            {
              key: 'emittedInvoicesSII',
              label: 'Emitido SII',
              customComponent: customInvoicingComponent,
              type: 'custom',
            },
            {
              key: 'emittedInvoicesPruff',
              label: 'Emitido Pruff',
              customComponent: customInvoicingComponent,
            },
            { key: 'businessIdentifier', label: 'RUT cliente' },
            { key: 'businessName', label: 'Nombre cliente' },
            { key: 'totalQuoteCost', label: 'Factoring', type: 'money' },
            { key: 'totalCreditCosts', label: 'Crédito', type: 'money' },
            { key: 'totalConfirmingCosts', label: 'Confirming', type: 'money' },
            { key: 'totalExtensionsCosts', label: 'Prórroga', type: 'money' },
            { key: 'totalCosts', label: 'Total neto', type: 'money' },
            { key: 'totalCostsIva', label: 'Total Iva', type: 'money' },
            {
              key: 'totalCostsWithIva',
              label: 'Total factura',
              type: 'money',
            },
            {
              key: 'amountToInvoiceByPruff',
              label: 'Monto a facturar',
              type: 'money',
            },
          ]}
          data={data}
          actions={[]}
          showActions
          rowsPerPageDefault={100}
          selected={selectedIds}
          setSelected={setSelectedIds}
          exportToFile="xlsx"
          canSelectAll
          finalSumRow={true}
        />
      )}
      {type === 'Facturado' && (
        <EnhancedTable
          data={alreadyInvoicedInvoices}
          dataHeaders={[
            {
              key: 'folio',
              label: 'Folio',
            },
            {
              key: 'docType',
              label: 'Tipo doc.',
            },
            { key: 'stakeholderName', label: 'Nombre deudor' },
            { key: 'stakeholderIdentifier', label: 'RUT deudor' },
            { key: 'amount', label: 'Monto', type: 'money' },
            { key: 'issuedDate', label: 'Emisión', type: 'date' },
            ...(invoicingPartner === 'cessionDTE' ? [handleCheckDoc] : []),
          ]}
          rowsPerPageDefault={100}
          lastElement
        />
      )}

      <ReportInvoicingConfirmDialog
        confirmOpen={openInvoiceDialog}
        setConfirmOpen={setOpenInvoiceDialog}
        selectedInvoices={selected}
        setSelectedInvoices={setSelected}
        startDate={startDate}
        setUp={setUp}
        endDate={endDate}
        setSuccessInvoices={setSuccessInvoices}
        setResultInvoices={setResultInvoices}
        selectedCertificate={selectedCertificate}
        setFinalizedModal={setFinalizedModal}
      />
      {finalizedModal && (
        <TheBillerFinalizedModal
          finalizedModal={finalizedModal}
          setFinalizedModal={setFinalizedModal}
          resultInvoices={resultInvoices}
          successInvoices={successInvoices}
          handleClose={handleClose}
        />
      )}
      <SubjectOptionsDialog
        open={subjectDialogOpen}
        onClose={() => setSubjectDialogOpen(false)}
        onConfirm={handleSubjectDialogConfirm}
        currentSubject="Servicios Factoring"
      />
      <DteStatusDialog
        open={dteStatusDialogOpen}
        onClose={() => setDteStatusDialogOpen(false)}
        data={dteStatus}
      />
    </div>
  );
};

export default InvoicingBook;
